import {
  Dispatch,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react";

import { Campaign, CampaignResult } from "models/CommonType";
import { useSearchCampaign } from "utils/apis";
import { QueryAction, QueryOption, SearchAction, SearchSort } from "./SearchReducer";
import CampaignList from "./CampaignList";

interface Props {
  queryOption: QueryOption;
  dispatchQuery: Dispatch<QueryAction>;
  setDataCount: (count: number | undefined) => void;
  closeHandler?: () => void;
}

const SearchFilterCampaign: React.ForwardRefRenderFunction<any, Props> = ({
  queryOption,
  dispatchQuery,
  setDataCount,
  closeHandler,
}, ref) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  useImperativeHandle(ref, () => {
    return {
      onKeywordChanged(keyword: string) {
        if (keyword !== queryOption.keyword) {
          setCampaigns([]);
        }
      },
      onSortChanged(sort?: SearchSort) {
        if (sort !== queryOption.sort) {
          setCampaigns([]);
        }
      }
    };
  }, [queryOption]);

  const { data, isLoading } = useSearchCampaign(queryOption);

  const { count, hasMore } = useMemo(() => {
    if (!data) {
      return { count: undefined, hasMore: false };
    }
    const result = (data as CampaignResult);
    setCampaigns(prevCampaigns => [...prevCampaigns, ...result.results]);
    return {
      count: result.meta.pagination.count,
      hasMore: result.meta.pagination.page < result.meta.pagination.pages,
    };
  }, [data]);

  const nextPageHandler = useCallback(() => {
    if (hasMore) {
      dispatchQuery({ type: SearchAction.ChangedPage });
    }
  }, [hasMore, dispatchQuery]);

  useEffect(() => setDataCount(count), [setDataCount, count]);

  return (
    <>
      {(isLoading || !!count) &&
        <CampaignList
          isLoading={isLoading}
          campaigns={campaigns}
          count={count}
          closeHandler={closeHandler}
          nextPageHandler={nextPageHandler} />}
    </>
  )
}

export default forwardRef(SearchFilterCampaign);
